import { CheckCheck, ChefHat, HandPlatter, QrCode, Undo2 } from "lucide-react";
import { useState } from "react";

import ItemLinha from "./ItemLinha";
import Timer from "./Timer";
import { rotuloEnvio } from "../lib/envios";
import { NIVEL_CLASSES, rotuloMesa } from "../lib/kds";

import { Button } from "@/components/ui/button";

const NIVEL = NIVEL_CLASSES.tranquilo;

/** Cartão da tela do Garçom: uma mesa, os pratos dela que saíram da cozinha.
 *
 *  A MESA é o que vem grande no topo — é a pergunta do garçom ("pra onde eu
 *  levo?"); o código do pedido fica pequeno. Cada prato tem o próprio botão
 *  Entregue, porque a bebida sai antes da comida e o garçom leva o que tem na
 *  mão. Em "Entregues" o botão vira desfazer.
 */
export default function CartaoGarcom({ card, entregues = false, onEntregar, onEntregarTudo, products, mobile }) {
  const [ocupado, setOcupado] = useState(null);
  const itens = card._itens || [];
  // Cancelado na mesa aparece riscado, sem botão; "Entregar tudo" só com os outros
  const paraEntregar = itens.filter((i) => !i._cancelado);
  const qr = card.modulo === "MesaQR";

  const findProductPhoto = (nome) => {
    if (!products || !nome) return null;
    const m = products.find((p) => (p.name || "").toLowerCase() === nome.toLowerCase());
    return m?.photo_url || m?.simple_photo_url || null;
  };

  const agir = async (chave, fn) => {
    setOcupado(chave);
    try { await fn(); } finally { setOcupado(null); }
  };

  return (
    <div className={`rounded-xl border-[3px] shadow-sm overflow-hidden flex flex-col ${NIVEL.card} ${entregues ? "opacity-80" : ""}`}>
      <div className={`flex items-center justify-between gap-2 px-3 sm:px-4 py-2.5 ${NIVEL.header} border-b border-black/5`}>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className={`text-xl font-black ${NIVEL.texto}`}>{rotuloMesa(card)}</span>
            {qr && (
              <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium bg-sky-100 text-sky-800">
                <QrCode className="w-3 h-3" />QR
              </span>
            )}
          </div>
          <span className={`text-[11px] ${NIVEL.suave}`}>
            {card.codigo}{rotuloEnvio(card) ? ` · ${rotuloEnvio(card)}` : ""}{card.cliente_nome && card.cliente_nome !== rotuloMesa(card) ? ` · ${card.cliente_nome}` : ""}
          </span>
        </div>
        {!entregues && <Timer since={card._desde} grande={mobile} atrasoMin={Infinity} />}
      </div>

      <div className="px-3 sm:px-4 py-2.5 flex-1">
        {itens.map((item) => (
          <div key={item._idx} className="flex items-start gap-2">
            <div className="flex-1 min-w-0">
              <ItemLinha item={item} nivel={NIVEL} naFila={false} findProductPhoto={findProductPhoto} produtos={products} />
            </div>
            {!item._cancelado && <Button
              size="sm" variant={entregues ? "ghost" : "default"} disabled={ocupado !== null}
              onClick={() => agir(item._idx, () => onEntregar(card, item, !entregues))}
              className={`shrink-0 gap-1.5 font-bold ${mobile ? "h-11 px-3" : "h-9"} ${entregues ? "text-zinc-500" : "bg-emerald-600 hover:bg-emerald-700 text-white"}`}
              title={entregues ? "Desfazer: o prato ainda não chegou na mesa" : "Marcar que este prato chegou na mesa"}
            >
              {ocupado === item._idx ? "..." : entregues
                ? <><Undo2 className="w-4 h-4" />Desfazer</>
                : <><HandPlatter className="w-4 h-4" />Entregue</>}
            </Button>}
          </div>
        ))}
      </div>

      {!entregues && card._naCozinha > 0 && (
        <div className={`px-3 sm:px-4 pb-2 flex items-center gap-1.5 text-xs font-semibold ${NIVEL.suave}`}>
          <ChefHat className="w-3.5 h-3.5 shrink-0" />
          +{card._naCozinha} {card._naCozinha === 1 ? "item ainda na cozinha" : "itens ainda na cozinha"}
        </div>
      )}

      {!entregues && paraEntregar.length > 1 && (
        <div className="px-3 pb-3">
          <Button
            disabled={ocupado !== null}
            onClick={() => agir("tudo", () => onEntregarTudo(card, paraEntregar))}
            className={`w-full gap-2 font-bold ${mobile ? "h-[52px] text-base" : "h-11 text-sm"} bg-zinc-900 hover:bg-zinc-800 text-white`}
          >
            {ocupado === "tudo" ? "..." : <><CheckCheck className="w-5 h-5" />Entregar tudo ({paraEntregar.length})</>}
          </Button>
        </div>
      )}
    </div>
  );
}
